import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserManage } from "./UserManage";
import { Button, Card, DialogActions, DialogContent } from "@mui/material";
import { Dialog } from "primereact/dialog";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { Claim, UserManageForm, UserManagePayload } from "./interfaces";
import { Customer } from "../shared/interfaces/Customer";
import { UserRoles } from "./UserRolesEnum";
import { validateEmail } from "../register/validateEmail";
import { useCreateUser } from "./useCreateUser";
import { useAddClaim } from "./useAddClaim";
import { RegistrationAlert, SnackSeverity } from "../register/SnackbarComponents";
import { Snackbar } from "../shared/Snackbar";
import { Region } from "../shared/interfaces/Region";
import { UserRolesConstants } from "../shared/constants/UserRolesConstants";

interface CreateUserModalProps {
  show: boolean;
  onClose: () => void;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  width: "50%",
  maxWidth: "none",
  maxHeight: "none",
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

function convertUserRoleStringToCorrectFormat(userRole: string) {
  userRole = userRole.replace(" ", "");
  userRole = userRole.charAt(0).toUpperCase() + userRole.slice(1);
  return userRole;
}
export function CreateUserModal(props: CreateUserModalProps) {
  const { t } = useTranslation();
  const header = <Typography variant="h5" component="p">{`${t("userManagement.manageUser")}`}</Typography>;
  const [customer, setCustomer] = useState<Customer | undefined>(undefined);
  const [company, setCompany] = useState<Region | undefined>(undefined);
  const [site, setSite] = useState<Region | undefined>(undefined);
  const [userRole, setUserRole] = useState<UserRoles | undefined>(undefined);
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [emailValid, setIsEmailValid] = useState<boolean>(true);
  const [customerValid, setIsCustomerValid] = useState<boolean>(true);
  const [userRoleValid, setIsUserRoleValid] = useState<boolean>(true);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
  }>({ open: false, message: "", severity: SnackSeverity.SUCCESS });

  const { show, onClose } = props;

  const createUser = useCreateUser();
  const addClaim = useAddClaim(handleSuccess);

  const { isError, error } = createUser;

  function handleSuccess() {
    setSnackbar({
      open: true,
      message: "User has been created successfully",
      severity: "success",
    });
  }

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleValidFormSubmission = async () => {
    const isValid = validateForm();
    if (isValid) {
      const userManageForm: UserManageForm = {
        email: email,
        userRole: userRole,
        customer: customer,
        company: company,
        site: site,
      };

      const updatedUserRole = convertUserRole(userManageForm);
      if (updatedUserRole != null) {
        const payload: UserManagePayload = {
          email: userManageForm.email ?? "",
          claims: [updatedUserRole],
        };

        createUser.mutate(payload, {
          onSuccess: payload.claims
            ? () => {
                addClaim.mutate(payload, {
                  onSuccess: handleSuccess,
                  onError: () => {
                    setSnackbar({
                      severity: "error",
                      message: t("userManagement.createUserError"),
                      open: true,
                    });
                  },
                });
              }
            : handleSuccess,
          onError: () => {
            setSnackbar({
              severity: "error",
              message: t("userManagement.addClaimError"),
              open: true,
            });
          },
        });
      }
    }
  };

  useEffect(() => {
    if (isError && error instanceof Error) {
      setSnackbar({ severity: "error", message: error.message, open: true });
    }
  }, [isError, error]);

  async function handleModalClose() {
    resetForm();
    onClose();
  }

  function resetForm() {
    setCustomer(undefined);
    setCompany(undefined);
    setSite(undefined);
    setUserRole(undefined);
    setEmail(undefined);
    setIsEmailValid(true);
    setIsCustomerValid(true);
    setIsUserRoleValid(true);
  }

  const isCustomerValid = (customer: Customer | undefined) => {
    const isCustomerValid = userRole === UserRoles.SuperAdmin || customer != null;
    setIsCustomerValid(isCustomerValid);
    return isCustomerValid;
  };

  const isUserRoleValid = (userRole: UserRoles | undefined) => {
    setIsUserRoleValid(userRole != null);
    return userRoleValid;
  };

  const isEmailValid = (email?: string) => {
    email != null && email.length > 0 ? setIsEmailValid(validateEmail(email)) : setIsEmailValid(false);
    return emailValid;
  };

  const validateForm = () => {
    let emailValid = isEmailValid(email);
    let customerValid = isCustomerValid(customer);
    let userRoleValid = isUserRoleValid(userRole);

    return emailValid && customerValid && userRoleValid;
  };

  function convertUserRole(data: UserManageForm): Claim | undefined {
    let userRole = "";

    if (data.userRole != null) {
      userRole = convertUserRoleStringToCorrectFormat(data.userRole);
    }

    if (userRole.toLowerCase().includes(UserRoles.AdminUser.toLowerCase())) {
      if (data.customer == null) {
        return undefined;
      }
      return {
        type: UserRolesConstants.CustomerUTPAdmin,
        value: data.customer.id,
      };
    }

    if (userRole.toLowerCase().includes(UserRoles.SuperAdmin.toLowerCase())) {
      return {
        type: UserRolesConstants.ManagedSurveysSuperAdmin,
        value: UserRolesConstants.ManagedSurveysSuperAdmin,
      };
    }

    if (data.customer == null) {
      return undefined;
    }

    if (data.company == null) {
      return { type: `Customer${userRole}`, value: data.customer.id };
    }

    if (data.site == null) {
      return { type: `Company${userRole}`, value: data.company?.id };
    }

    return { type: `Site${userRole}`, value: data.site?.id };
  }

  return (
    <Card>
      <StyledDialog header={header} visible={show} onHide={handleModalClose} baseZIndex={1000}>
        <DialogContent>
          <UserManage
            title={t("userManagement.createUser")}
            customer={customer}
            company={company}
            site={site}
            userRole={userRole}
            email={email}
            onCustomerSelect={(value) => {
              setCustomer(value);
              isCustomerValid(value);
            }}
            onCompanySelect={setCompany}
            onSiteSelect={setSite}
            onUserRoleSelect={(value) => {
              setUserRole(value);
              isUserRoleValid(value);
            }}
            onEmailSelect={(value) => {
              setEmail(value);
              isEmailValid(value);
            }}
            isCustomerValid={customerValid}
            isUserRoleValid={userRoleValid}
            isEmailValid={emailValid}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleModalClose}>
            {t("common.close")}
          </Button>
          <Button variant="contained" onClick={handleValidFormSubmission}>
            {t("common.save")}
          </Button>
        </DialogActions>
        <Snackbar open={snackbar.open} message={snackbar.message} onClose={handleSnackbarClose}>
          <RegistrationAlert onClose={handleSnackbarClose} severity={snackbar.severity}>
            {snackbar.message}
          </RegistrationAlert>
        </Snackbar>
      </StyledDialog>
    </Card>
  );
}
